import React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CartItem as CartItemType } from '../../types';
import { getImageUrl } from '../../utils/imageUrl';
import './CartItem.css';

interface CartItemProps {
  item: CartItemType;
}

const CartItem: React.FC<CartItemProps> = ({ item }) => {
  const { t, i18n } = useTranslation();
  const { updateQuantity, removeItem } = useCart();
  const { product, quantity } = item;

  const currentLang = i18n.language;
  const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
  const price = product.final_price || product.price;
  const subtotal = price * quantity;

  const productImage = getImageUrl(
    product.image || product.primary_image || product.images?.[0]?.image
  );

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity > 0 && newQuantity <= product.stock) {
      updateQuantity(product.id, newQuantity);
    }
  };

  return (
    <div className="cart-item">
      <div className="cart-item-image">
        <img src={productImage} alt={productName} />
      </div>

      <div className="cart-item-details">
        <h3>{productName}</h3>
        <div className="cart-item-meta" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '2px' }}>
          <span className="cart-item-unit-price" style={{ fontWeight: 600, fontSize: '14px', color: 'var(--primary, #7A0C0C)' }}>
            ৳{price.toFixed(2)}
          </span>
          {product.discount_percentage > 0 && (
            <span className="discount-badge" style={{ margin: 0 }}>{product.discount_percentage}% OFF</span>
          )}
        </div>
        {(item.selected_color || item.selected_size || item.selected_weight) && (
          <div className="cart-item-variant-list">
            {item.selected_color && (
              <span className="cart-item-variant-badge color">
                <span className="color-dot" style={{ backgroundColor: item.selected_color }} />
                {item.selected_color}
              </span>
            )}
            {item.selected_size && (
              <span className="cart-item-variant-badge size">
                {item.selected_size}
              </span>
            )}
            {item.selected_weight && (
              <span className="cart-item-variant-badge weight">
                {item.selected_weight}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="cart-item-quantity">
        <button 
          onClick={() => handleQuantityChange(quantity - 1)}
          disabled={quantity <= 1}
          title="Decrease quantity"
        >
          <Minus size={14} />
        </button>
        <span>{quantity}</span>
        <button 
          onClick={() => handleQuantityChange(quantity + 1)}
          disabled={quantity >= product.stock}
          title="Increase quantity"
        >
          <Plus size={14} />
        </button>
      </div>

      <div className="cart-item-subtotal">
        ৳{subtotal.toFixed(2)}
      </div>

      <div className="cart-item-remove">
        <button 
          onClick={() => removeItem(product.id)}
          className="remove-btn"
          title={t('cart.remove') || 'Remove'}
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

export default CartItem;
